import { useFormik } from 'formik';
// mantine
import {
    Button,
    Checkbox,
    Divider,
    TextInput,
    UnstyledButton
} from '@mantine/core';
// icons
import { RefreshCcw } from 'lucide-react';
// components
import { getBrowserTimezoneName } from '@newo/toolkit/datetime';
import { processFormData } from '@newo/toolkit/bff';
import { IActor } from '@newo/api/gateway';
import IntergrationFields from '@/modules/Integrations/components/IntegrationFields/IntegrationFields.tsx';
// utils
import { generateRandomString } from '@/utils';
import validationSchema from '@/modules/Sandbox/components/AddPersonaForm/AddPersonaForm.schema.ts';
import { IChatPersona } from '@/modules/Chat/Chat.types.ts';
import {
    useCreatePersonaMutation,
    useCreatePersonaActorMutation
} from '@/services/customer/personas/personas.queries.ts';

interface AddPersonaFormProps {
    currentActor?: IActor;
    onClose: () => void;
    onSubmit: (persona: IChatPersona, actor?: IActor) => void;
}

const EXTERNAL_ID_LENGTH = 6;

const AddPersonaForm = ({
    currentActor,
    onClose,
    onSubmit
}: AddPersonaFormProps) => {
    const { mutate: createPersona } = useCreatePersonaMutation();
    const { mutate: createActor } = useCreatePersonaActorMutation();

    const initialValues = {
        name: '',
        withActor: true,
        integration_idn: currentActor?.integration_idn ?? 'sandbox',
        connector_idn: currentActor?.connector_idn ?? '',
        external_id: generateRandomString(EXTERNAL_ID_LENGTH)
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData(formData);
            createPersona(
                {
                    data: {
                        name: data.name,
                        title: data.name
                    }
                },
                {
                    onSuccess: (newPersona) => {
                        if (!data.withActor) {
                            onSubmit({ name: data.name, id: newPersona.id });
                            onClose();
                            return;
                        }

                        createActor(
                            {
                                personaId: newPersona.id,
                                data: {
                                    name: data.name,
                                    external_id: data.external_id,
                                    integration_idn: data.integration_idn,
                                    connector_idn: data.connector_idn,
                                    time_zone_identifier:
                                        getBrowserTimezoneName()
                                }
                            },
                            {
                                onSuccess: (newActor) => {
                                    onSubmit(
                                        { name: data.name, id: newPersona.id },
                                        {
                                            name: data.name,
                                            id: newActor.id,
                                            integration_idn:
                                                data.integration_idn,
                                            connector_idn: data.connector_idn,
                                            external_id: data.external_id
                                        } as IActor
                                    );
                                    onClose();
                                }
                            }
                        );
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onClose();
    };

    const handleIntegrationChange = (value: string | null) => {
        formik.setFieldValue('integration_idn', value);
        formik.setFieldValue('connector_idn', undefined);
    };

    const handleConnectorChange = (value: string | null) => {
        formik.setFieldValue('connector_idn', value);
    };

    const handleAutogenerate = () => {
        formik.setFieldValue(
            'external_id',
            generateRandomString(EXTERNAL_ID_LENGTH)
        );
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                error={
                    formik.touched.name && formik.values.name
                        ? formik.errors.name
                        : undefined
                }
                label="Name"
                name="name"
                placeholder="Personas's name"
                value={formik.values.name}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <Checkbox
                checked={formik.values.withActor}
                label="Create actor"
                size="sm"
                onChange={(event) => {
                    formik.setFieldValue(
                        'withActor',
                        event.currentTarget.checked
                    );
                }}
            />

            {formik.values.withActor ? (
                <>
                    <Divider className="my-4" />
                    <div className="text-sm font-semibold">Actor settings:</div>
                    <IntergrationFields
                        connectorIdn={formik.values.connector_idn}
                        integrationIdn={formik.values.integration_idn}
                        onConnectorChange={handleConnectorChange}
                        onIntegrationChange={handleIntegrationChange}
                    />
                    <TextInput
                        error={
                            formik.touched.external_id &&
                            formik.values.external_id
                                ? formik.errors.external_id
                                : undefined
                        }
                        label="External Id"
                        name="external_id"
                        placeholder="External Id"
                        rightSection={
                            <UnstyledButton
                                title="Autogenerate"
                                onClick={handleAutogenerate}
                            >
                                <RefreshCcw />
                            </UnstyledButton>
                        }
                        value={formik.values.external_id}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </>
            ) : undefined}
            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Create and apply
                </Button>
            </div>
        </form>
    );
};

export default AddPersonaForm;
